/* ═══════════════════════════════════════════════════════════════════════
   Typed API client — all backend calls centralised here
   ═══════════════════════════════════════════════════════════════════════ */

import type {
  CompanyListResponse,
  SummaryResponse,
  TrendResponse,
  ComparisonResponse,
  IngestRequest,
  IngestResponse,
  ConceptSearchResponse,
  ConceptCatalogResponse,
  AliasesResponse,
  TickerUniverseListResponse,
  QuoteResponse,
  PriceHistoryResponse,
  Company,
  FilingListResponse,
  InsiderListResponse,
  InsiderTradeListResponse,
  InstitutionalHolderListResponse,
} from "../types";
import type { UserProfile } from "../store/auth";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${path}`);
  return res.json() as Promise<T>;
}

// ── Companies ────────────────────────────────────────────────────────

export function fetchCompanies() {
  return apiFetch<CompanyListResponse>("/api/companies");
}

// ── Insiders & Holders ───────────────────────────────────────────────

export function fetchInsiders(ticker: string) {
  return apiFetch<InsiderListResponse>(`/api/companies/${ticker}/insiders`);
}

export function fetchInsiderTrades(ticker: string) {
  return apiFetch<InsiderTradeListResponse>(
    `/api/companies/${ticker}/insider-trades`,
  );
}

export function fetchInstitutionalHolders(ticker: string) {
  return apiFetch<InstitutionalHolderListResponse>(
    `/api/companies/${ticker}/institutional-holders`,
  );
}

export function fetchFilings(ticker: string) {
  return apiFetch<FilingListResponse>(`/api/companies/${encodeURIComponent(ticker)}/filings`);
}

// ── Summary ──────────────────────────────────────────────────────────

export function fetchSummary(exchange: string, ticker: string) {
  return apiFetch<SummaryResponse>(`/api/financials/${exchange}/${ticker}/summary`);
}

// ── Trend ────────────────────────────────────────────────────────────

export function fetchTrend(
  exchange: string,
  ticker: string,
  concept: string,
  periods = 8,
  form?: string,
) {
  const params = new URLSearchParams({ periods: String(periods) });
  if (form) params.set("form", form);
  return apiFetch<TrendResponse>(
    `/api/financials/${exchange}/${ticker}/trend/${concept}?${params}`,
  );
}

// ── Compare ──────────────────────────────────────────────────────────

export function fetchCompare(
  exchange: string,
  concept: string,
  tickers: string[],
  periods = 1,
  form?: string,
) {
  const params = new URLSearchParams({
    tickers: tickers.join(","),
    periods: String(periods),
  });
  if (form) params.set("form", form);
  return apiFetch<ComparisonResponse>(
    `/api/financials/${exchange}/compare/${concept}?${params}`,
  );
}

// ── Ingestion ────────────────────────────────────────────────────────

export function ingestTickers(body: IngestRequest) {
  return apiFetch<IngestResponse>("/api/ingest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── Concepts (unused by frontend currently, included for completeness) ─

export function searchConcepts(keyword?: string, ticker?: string, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (keyword) params.set("keyword", keyword);
  if (ticker) params.set("ticker", ticker);
  return apiFetch<ConceptSearchResponse>(
    `/api/financials/concepts/search?${params}`,
  );
}

export function fetchAliases() {
  return apiFetch<AliasesResponse>("/api/financials/concepts/aliases");
}

// ── Concept Catalog ──────────────────────────────────────────────────

export function fetchConceptCatalog(exchange: string, ticker: string, limit = 500) {
  const params = new URLSearchParams({ limit: String(limit) });
  return apiFetch<ConceptCatalogResponse>(
    `/api/financials/${exchange}/${ticker}/concepts?${params}`,
  );
}

// ── Re-ingest ────────────────────────────────────────────────────────

export function reingestTicker(ticker: string) {
  return apiFetch<IngestResponse>(`/api/ingest/reingest/${ticker}`, {
    method: "POST",
  });
}

// ── Ticker Universe ──────────────────────────────────────────────────

export function searchTickers(search: string, perPage = 10) {
  const params = new URLSearchParams({
    search,
    per_page: String(perPage),
  });
  return apiFetch<TickerUniverseListResponse>(`/api/tickers?${params}`);
}

// ── Quotes ───────────────────────────────────────────────────────────

export function fetchQuote(ticker: string) {
  return apiFetch<QuoteResponse>(`/api/quotes/${ticker}`);
}

// ── Price History ────────────────────────────────────────────────────

export function fetchPriceHistory(ticker: string, period = "1y") {
  return apiFetch<PriceHistoryResponse>(
    `/api/quotes/${encodeURIComponent(ticker)}/history?period=${encodeURIComponent(period)}`,
  );
}

// ── Peers ─────────────────────────────────────────────────────────────

export function fetchPeers(ticker: string) {
  return apiFetch<Company[]>(`/api/companies/${encodeURIComponent(ticker)}/peers`);
}

// ── Export (NotebookLM) ──────────────────────────────────────────────

export function fetchExportUrl(
  ticker: string,
  periods = 10,
  form = "10-K",
): string {
  const params = new URLSearchParams({
    periods: String(periods),
    form,
  });
  return `/api/export/${encodeURIComponent(ticker)}?${params}`;
}

// ── Auth ─────────────────────────────────────────────────────────────

export function verifyGoogleToken(access_token: string) {
  return apiFetch<UserProfile>("/api/auth/google", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token }),
  });
}

export function fetchCurrentUser() {
  return apiFetch<UserProfile>("/api/auth/me");
}

export function logout() {
  return apiFetch<{ status: string }>("/api/auth/logout", {
    method: "POST",
  });
}

// ── Preferences ──────────────────────────────────────────────────────

export function fetchPreferences() {
  return apiFetch<Record<string, any>>("/api/preferences");
}

export function savePreference(key: string, value: any) {
  return apiFetch<{ status: string; key: string }>(`/api/preferences/${key}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(value),
  });
}

export function deletePreference(key: string) {
  return apiFetch<{ status: string; key: string }>(`/api/preferences/${key}`, {
    method: "DELETE",
  });
}

// ── Templates ────────────────────────────────────────────────────────

import type { MetricTemplate } from "../hooks/useMetricTemplates";

export function fetchTemplates() {
  return apiFetch<MetricTemplate[]>("/api/templates");
}

export function createTemplate(template: Omit<MetricTemplate, "id" | "isGlobal">) {
  return apiFetch<MetricTemplate>("/api/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(template),
  });
}

export function deleteTemplate(templateId: string) {
  return apiFetch<{ status: string }>(`/api/templates/${templateId}`, {
    method: "DELETE",
  });
}

export { ApiError };
